import {
  DatePicker,
  Space,
  Tooltip,
  InputNumber,
  Input,
  Radio,
  Checkbox,
  Button,
  Select,
  Switch,
  Form,
  notification
} from 'antd';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { QuestionCircleOutlined } from '@ant-design/icons';
import numeral from 'numeral';
import { preRequisitesLayout } from '../../utils/constants/formLayout';
import PreRequisitesSwitch from './PreRequisitesSwitch';
import useQuotesContext from './QuotesContext';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import ComparisonTableMRI from './ComparisonTableMRI';
import {
  StandardIcon,
  HouseIcon,
  OfficeIcon,
  CommercialIcon,
  OtherBuildingIcon,
  ConstructionIcon,
  ConstructionLongIcon
} from '../../utils/constants/formIcons';
import GoogleMapsIcon from '../../assets/images/icons/Google_Maps_icon.png';
import { isCorseZipcode, isMetropolitanZipcode } from '../../utils/zipcode';

const { Option } = Select;
const dateFormat = 'DD/MM/YYYY';

const usePNOMLFields = (form) => {
  const {
    exemptions,
    formValues,
    setFormValues,
    enums,
    postalCode,
    setPostalCode,
    estimatedCost,
    catNat,
    errorGetCities,
    setErrorGetCities
  } = useQuotesContext();
  const { t } = useTranslation();
  const { dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();
  const [isLoading, setIsLoading] = useState(false);
  const [isCityLoading, setIsCityLoading] = useState(false);
  const [addressSuggestion, setAddressSuggestion] = useState([]);
  const [cities, setCities] = useState([]);
  const number_activity = form.getFieldValue('number_activity');
  const isAmendment = window.location.href.search('amendment');
  const [checkedVal, setCheckedVal] = useState('');
  const [displayFurnishedRental, setDisplayFurnishedRental] = useState(false);
  const { subPath: type } = useParams();

  const openNotificationWithIcon = (notificationType, triggeredBy) => {
    if (triggeredBy === 'value') {
      notification[notificationType]({
        message: 'Notification de dérogation',
        description: t('help.activities'),
        duration: 20,
        key: 'oneTime'
      });
    } else {
      notification[notificationType]({
        message: t('help.surface_pno_title'),
        description: t('help.surface_pno'),
        duration: 20,
        key: 'oneTime'
      });
    }
  };

  const handleDisplayFurnishedRentalChange = (value) => {
    if (value) {
      setDisplayFurnishedRental(true);
      setFormValues({
        ...formValues,
        furnished_rental: 10000
      });
      form.setFieldsValue({
        furnished_rental: 10000
      });
    } else {
      setFormValues({
        ...formValues,
        furnished_rental: 0
      });
      form.setFieldsValue({
        furnished_rental: 0
      });
      setDisplayFurnishedRental(false);
    }
  };

  const isAllowedZipcode = (value) => {
    return isCorseZipcode(value) || isMetropolitanZipcode(value);
  };

  useEffect(() => {
    if (exemptions.includes(checkedVal)) {
      openNotificationWithIcon('warning', 'value');
    } // eslint-disable-next-line
  }, [exemptions.length]);

  useEffect(() => {
    if (formValues?.surface_area > 300 && type === 'pnoml') {
      openNotificationWithIcon('info', 'surface');
      form.setFieldsValue({
        surface_area: 300
      });
      setFormValues({ ...formValues, surface_area: 300 });
    }
  }, [formValues?.surface_area]);

  useEffect(() => {
    setFormValues({ ...formValues, furniture_capital: undefined });
    // eslint-disable-next-line
  }, [form.getFieldValue('risk_typology')]);

  useEffect(() => {
    if (number_activity) {
      const activities_trade = [];
      for (let i = 0; i < formValues.number_activity; i += 1) {
        activities_trade[i] = form.getFieldValue('activities_trade')?.[i] || '';
      }
      form.setFieldsValue({ activities_trade });
    } else form.setFieldsValue({ activities_trade: [] });
    // eslint-disable-next-line
  }, [number_activity, estimatedCost]);

  useEffect(() => {
    if (estimatedCost && estimatedCost.cat_nat_risk) {
      form.setFieldsValue({ cat_nat_risk: estimatedCost.cat_nat_risk });
      setFormValues({
        ...formValues,
        cat_nat_risk: estimatedCost.cat_nat_risk
      });
    }
    // eslint-disable-next-line
  }, [catNat]);

  const getCommuneCode = (value, key) => {
    try {
      setIsLoading(true);
      if (value) {
        const communCode = cities.find(
          (city) => city.nom === value.split(' (')[0]
        ).code;
        form.setFieldsValue({
          buildings: form.getFieldValue('buildings').map((values, index) =>
            index === key
              ? {
                  ...values,
                  address: {
                    ...values.address,
                    commune_code: communCode
                  }
                }
              : { ...values }
          )
        });
        setFormValues({
          ...formValues,
          buildings: form.getFieldValue('buildings').map((values, index) =>
            index === key
              ? {
                  ...values,
                  address: {
                    ...values.address,
                    commune_code: communCode
                  }
                }
              : { ...values }
          )
        });
      }
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsLoading(false);
  };

  const setAddress = (key, data, commune_code) =>
    form.getFieldValue('buildings').map((values, index) =>
      index === key
        ? {
            ...values,
            address: {
              number: data.result.address_components[0].long_name,
              street: data.result.address_components[1].long_name,
              administrative_area_level_2:
                data.result.address_components[3].long_name,
              postal_code: data.result.address_components[6].long_name,
              city: data.result.address_components[2].long_name,
              formatted_address: data.result.formatted_address,
              lat: data.result.geometry.location.lat,
              lng: data.result.geometry.location.lng,
              commune_code,
              address_autocomplete: ''
            }
          }
        : { ...values }
    );

  const getCity = async (postal, placeIdData, key) => {
    setIsCityLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/insee/cities/zipcode/${postal}`
      });

      const tmpData = data
        .map((city) => {
          return {
            nom: city.name.toUpperCase(),
            codePostal: city.zipcode,
            code: city.code
          };
        })
        .flat();
      setCities(tmpData);
      setIsCityLoading(false);
      setErrorGetCities(false);
      let commune_code = '';
      if (placeIdData) {
        if (tmpData) {
          commune_code = tmpData.find(
            (city) =>
              placeIdData.result.address_components[2].long_name.toUpperCase() ===
              city.nom
          ).code;
        }
        form.setFieldsValue({
          buildings: setAddress(key, placeIdData, commune_code)
        });
        setFormValues({
          ...formValues,
          buildings: setAddress(key, placeIdData, commune_code)
        });
      }
      return tmpData;
    } catch (e) {
      if (e.response) {
        message(e.response.status);
      }
      setIsCityLoading(false);
      const isNotFoundError = e.response?.status === 404;
      setErrorGetCities(!isNotFoundError);
      return [];
    }
  };

  const handlePropertyUnderConstructionChange = (value) => {
    const updatedFormValues = {
      ...formValues,
      property_under_construction: value
    };
    delete updatedFormValues.under_construction_duration;
    setFormValues({ ...updatedFormValues });
    form.setFieldsValue({
      property_under_construction: value,
      under_construction_duration: undefined
    });
  };

  const handleMainUseChange = (value) => {
    const updatedFormValues = {
      ...formValues,
      main_use: value?.target?.value
    };
    form.setFieldsValue({
      main_use: value?.target?.value
    });
    if (
      value?.target?.value !== 'rec_HOUSE' ||
      value?.taget?.value !== 'rec_APPARTMENT'
    ) {
      updatedFormValues.squat_option = false;
      form.setFieldsValue({
        squat_option: false
      });
    }
    if (
      value?.target?.value === 'rec_BOX_PARKING' ||
      value?.taget?.value === 'rec_OPEN_PARKING'
    ) {
      updatedFormValues.surface_area = 20;
      updatedFormValues.deductible_cost_index = '0,25x';
      form.setFieldsValue({
        surface_area: 20,
        deductible_cost_index: '0,25x'
      });
    }
    setFormValues({ ...updatedFormValues });
  };

  const handlePostalCodeChange = async (event, key) => {
    const { value } = event.target;

    const newBuildingsArr = form
      .getFieldValue('buildings')
      .map((values, index) =>
        index === key
          ? {
              ...values,
              address: {
                ...values.address,
                city: undefined
              }
            }
          : { ...values }
      );

    form.setFieldValue(['buildings', key, 'address', 'city'], undefined);
    setFormValues({
      ...formValues,
      buildings: newBuildingsArr
    });

    if (isAllowedZipcode(value)) {
      setPostalCode(value);
      const tmpCities = await getCity(value);
      if (tmpCities && tmpCities.length > 0) {
        const filteredCities = tmpCities.filter((city) => {
          let isMatching = false;

          if (city.codePostal.startsWith(value)) {
            isMatching = true;
          }

          return isMatching;
        });

        const newBuildingsArr = form
          .getFieldValue('buildings')
          .map((values, index) =>
            index === key
              ? {
                  ...values,
                  address: {
                    ...values.address,
                    city: `${filteredCities[0].nom} (${filteredCities[0].codePostal})`
                  }
                }
              : { ...values }
          );

        form.setFieldValue(
          ['buildings', key, 'address', 'city'],
          `${filteredCities[0].nom} (${filteredCities[0].codePostal})`
        );
        setFormValues({
          ...formValues,
          buildings: newBuildingsArr
        });
      }
    }
  };

  const getAddressAutocomplete = async (address_autocomplete) => {
    if (address_autocomplete) {
      setIsLoading(true);
      try {
        const { data } = await dispatchAPI('GET', {
          url: `/places/${address_autocomplete}`
        });
        setAddressSuggestion(data?.predictions);
      } catch (e) {
        if (e.response) message(e.response.status);
      }
      setIsLoading(false);
    }
  };

  const getAddressPlaceId = async (placeId, key) => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/places/placeid/${placeId}`
      });
      if (data) {
        const postalCodeFind = data.result.address_components.find(
          (component) => component.types.includes('postal_code')
        )?.long_name;
        if (postalCodeFind) {
          await getCity(postalCodeFind, data, key);
        }
      }
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsLoading(false);
  };

  const pnomlFields = [
    {
      name: ['buildings'],
      noLabel: true,
      noStyle: true,
      tooltip: true,
      tooltipName: 'buildings_with_corsica',
      input: (
        <Form.List name="buildings" initialValue={[{}]}>
          {(fields, { remove }) => (
            <>
              {fields.map((field) => {
                return (
                  <div className="mri-building-address-wrapper">
                    <Form.Item
                      {...field}
                      key={[field.name, 'address', 'address_autocomplete']}
                      name={[field.name, 'address', 'address_autocomplete']}
                      label={t('contracts.form.address.address_autocomplete')}
                      hasFeedback={isLoading}
                      {...(isLoading ? { validateStatus: 'validating' } : {})}
                    >
                      <Select
                        disabled={field.name !== 0}
                        filterOption={false}
                        showSearch
                        showArrow={false}
                        notFoundContent={null}
                        onSearch={getAddressAutocomplete}
                        onSelect={async (selected) => {
                          const placeId = selected.split('#')[0];
                          await getAddressPlaceId(placeId, field.name);
                        }}
                        options={
                          addressSuggestion &&
                          addressSuggestion.map((predictions) => ({
                            label: predictions.description,
                            value: `${predictions.place_id}#${predictions.description}`
                          }))
                        }
                        autoComplete="anyrandomstring"
                      />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'address', 'number']}
                      key={[field.name, 'address', 'number']}
                      label={t('contracts.form.address.number')}
                      rules={[
                        { required: true, message: t('errors.form.REQUIRED') }
                      ]}
                    >
                      <InputNumber />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'address', 'street']}
                      key={[field.name, 'address', 'street']}
                      label={t('contracts.form.address.street')}
                      rules={[
                        { required: true, message: t('errors.form.REQUIRED') }
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'address', 'additional']}
                      key={[field.name, 'address', 'additional']}
                      label={t('contracts.form.address.additional')}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...field}
                      name={[field.name, 'address', 'postal_code']}
                      key={[field.name, 'address', 'postal_code']}
                      label={t('contracts.form.address.postal_code')}
                      help={
                        exemptions.includes('administrative_area_level_2')
                          ? t('help.exemptions')
                          : null
                      }
                      rules={[
                        {
                          required: true,
                          message: t('errors.form.REQUIRED')
                        },
                        {
                          validator(_, value) {
                            if (!isAllowedZipcode(value)) {
                              return Promise.reject(
                                new Error(
                                  'Le code postal doit être un code métropolitain ou corse composé de 5 chiffres.'
                                )
                              );
                            }
                            return Promise.resolve();
                          }
                        }
                      ]}
                    >
                      <Input
                        style={{ width: '90px' }}
                        onChange={(event) =>
                          handlePostalCodeChange(event, field.name)
                        }
                        autoComplete="off"
                      />
                    </Form.Item>

                    <Form.Item
                      {...field}
                      name={[field.name, 'address', 'commune_code']}
                      key={[field.name, 'address', 'commune_code']}
                      label={t('contracts.form.commune_code')}
                      hidden
                    >
                      <Input disabled style={{ width: '90px' }} />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'address', 'city']}
                      key={[field.name, 'address', 'city']}
                      label={t('contracts.form.address.city')}
                      hasFeedback={isCityLoading}
                      {...(isCityLoading
                        ? { validateStatus: 'validating' }
                        : {})}
                      rules={[
                        { required: true, message: t('errors.form.REQUIRED') }
                      ]}
                    >
                      {!errorGetCities ? (
                        <Select
                          showSearch
                          onSelect={(selected) => {
                            getCommuneCode(selected, field.name);
                          }}
                          options={
                            cities &&
                            cities
                              .filter((city) => {
                                let isMatching = false;

                                if (city.codePostal.startsWith(postalCode)) {
                                  isMatching = true;
                                }

                                return isMatching;
                              })
                              .map((city) => ({
                                label: `${city.nom} (${city.codePostal})`,
                                value: `${city.nom} (${city.codePostal})`
                              }))
                          }
                        />
                      ) : (
                        <Input />
                      )}
                    </Form.Item>
                    {formValues &&
                      formValues?.buildings &&
                      formValues?.buildings[0]?.address && (
                        <>
                          <a
                            className="form-map-link mri-map-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${
                              formValues?.buildings[0].address &&
                              formValues?.buildings[0].address.lat
                            }0%2C${
                              formValues?.buildings[0].address &&
                              formValues?.buildings[0].address.lng
                            }&heading=-45&pitch=38&fov=80`}
                          >
                            {`Visualiser l'adresse `}
                            <img src={GoogleMapsIcon} alt="icone Google Maps" />
                          </a>
                        </>
                      )}
                    {formValues?.buildings?.length &&
                      formValues?.buildings?.length > 1 &&
                      field.name !== 0 && (
                        <>
                          <Form.Item>
                            <Button
                              style={{ color: 'purple' }}
                              type="link"
                              danger
                              onClick={() => remove(field.name)}
                            >
                              {`${t('buttons.removeBuilding')} ${
                                field.name + 1
                              }`}
                            </Button>
                          </Form.Item>
                        </>
                      )}
                  </div>
                );
              })}
            </>
          )}
        </Form.List>
      ),
      step: 0
    },
    {
      name: ['main_use'],
      rules: [{ required: true }],
      tooltip: true,
      tooltipName: 'main_use_pno',
      input: (
        <>
          <Form.Item name="main_use" noStyle>
            <Radio.Group
              buttonStyle="solid"
              className="radio-with-icon radio-with-icon-text-wrap radio-with-icon-row-wrap"
              onChange={(value) => handleMainUseChange(value)}
            >
              <Radio.Button value="rec_APPARTMENT">
                <HouseIcon />
                {t('contracts.tags.rec_APPARTMENT')}
              </Radio.Button>
              <Radio.Button value="rec_OFFICE">
                <OfficeIcon />
                {t('contracts.tags.rec_OFFICE')}
              </Radio.Button>
              <Radio.Button value="rec_HOUSE">
                <HouseIcon />
                {t('contracts.tags.rec_HOUSE')}
              </Radio.Button>
              <Radio.Button
                value="rec_COMMERCIAL_PREMISE"
                className="radio-with-icon-long-text"
              >
                <CommercialIcon />
                {t('contracts.tags.rec_COMMERCIAL_PREMISE')}
              </Radio.Button>
              <Radio.Button value="rec_BOX_PARKING">
                <StandardIcon />
                {t('contracts.tags.rec_BOX_PARKING')}
              </Radio.Button>
              <Radio.Button value="rec_OPEN_PARKING">
                <OtherBuildingIcon />
                {t('contracts.tags.rec_OPEN_PARKING')}
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
        </>
      ),
      help: exemptions.includes('main_use') ? t('help.exemptions') : null,
      step: 0
    },
    {
      name: ['surface_area'],
      label: ['surface_mri_ml'],
      tooltip: true,
      tooltipName: 'surface_pno',
      rules: [
        { required: true },
        {
          validator: (_, value) =>
            formValues && value > 0
              ? Promise.resolve()
              : Promise.reject(new Error('Ce champs est requis.'))
        }
      ],
      input: (
        <InputNumber
          min={0}
          disabled={
            formValues?.main_use === 'rec_BOX_PARKING' ||
            formValues?.main_use === 'rec_OPEN_PARKING'
          }
        />
      ),
      help: exemptions.includes('surface_area') ? t('help.exemptions') : null,
      step: 0
    },
    {
      name: ['risk_been_insured'],
      input: <PreRequisitesSwitch />,
      valuePropName: 'checked',
      step: 0
    },
    ...(formValues && formValues.risk_been_insured
      ? [
          {
            name: ['last_company_name'],
            input: <Input style={{ width: 500 }} />,
            rules: [{ required: true }],
            step: 0
          },
          {
            name: ['last_contract_number'],
            input: <Input style={{ width: 500 }} />,
            step: 0
          },
          {
            name: ['last_termination_date'],
            input: <DatePicker format={dateFormat} style={{ minWidth: 200 }} />,
            step: 0
          }
        ]
      : []),
    {
      name: ['number_claims'],
      rules: [{ required: true }],
      tooltip: true,
      input: <InputNumber min={0} />,
      help: exemptions.includes('number_claims') ? t('help.exemptions') : null,
      step: 0
    },
    ...(formValues && formValues.number_claims >= 1
      ? [
          {
            name: ['claims'],
            noLabel: false,
            step: 0,
            help: exemptions.includes(`claim_amount`)
              ? t('help.exemptions')
              : null,
            input: (
              <Form.List name="claims">
                {(fields) => (
                  <div>
                    {fields.map((field) => (
                      <Space
                        key={field.key}
                        style={{ display: 'flex', marginBottom: 8 }}
                        align="baseline"
                      >
                        <Form.Item
                          noStyle
                          {...field}
                          name={[field.name, 'claim_date']}
                          key={[field.fieldKey, 'claim_date']}
                          rules={[
                            {
                              required: true,
                              message: 'Date du sinistre manquant'
                            }
                          ]}
                        >
                          <DatePicker
                            format={dateFormat}
                            style={{ minWidth: 200 }}
                            disabledDate={(current) => {
                              return current >= moment();
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          noStyle
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...field}
                          name={[field.name, 'claim_type']}
                          key={[field.fieldKey, 'claim_type']}
                          rules={[
                            {
                              required: true,
                              message: 'Type du sinistre manquant'
                            }
                          ]}
                        >
                          <Input
                            style={{ width: '200px' }}
                            placeholder="Type de sinistre"
                          />
                        </Form.Item>
                        <Form.Item
                          noStyle
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...field}
                          name={[field.name, 'claim_amount']}
                          key={[field.fieldKey, 'claim_amount']}
                          rules={[
                            {
                              required: true,
                              message: 'Montant du sinistre manquant'
                            }
                          ]}
                        >
                          <InputNumber
                            min={0}
                            style={{ width: '200px' }}
                            formatter={(value) => value && `${value}€`}
                            parser={(value) => value.replace('€', '')}
                            placeholder="Montant du sinistre"
                          />
                        </Form.Item>
                      </Space>
                    ))}
                  </div>
                )}
              </Form.List>
            )
          }
        ]
      : []),
    {
      name: ['termination_by_company'],
      input: <PreRequisitesSwitch />,
      valuePropName: 'checked',
      step: 0,
      tooltip: true
    },
    ...(formValues && formValues.termination_by_company
      ? [
          {
            name: ['termination_reason_by_company'],
            rules: [{ required: true }],
            input: (
              <Select style={{ width: 220 }}>
                {enums?.termination_reason_by_company &&
                  enums.termination_reason_by_company.map((e) => (
                    <Option value={e} key={e}>
                      {t(`contracts.form.${e}`)}
                      <Tooltip
                        title={t(`contracts.form.tooltip.${e}`)}
                        overlayInnerStyle={{ width: 500 }}
                      >
                        <QuestionCircleOutlined style={{ float: 'right' }} />
                      </Tooltip>
                    </Option>
                  ))}
              </Select>
            ),
            help: exemptions.includes('termination_by_company')
              ? t('help.exemptions')
              : null,
            step: 0
          }
        ]
      : []),
    ...(formValues &&
    formValues.termination_reason_by_company === 'termination_other'
      ? [
          {
            name: ['termination_reason_by_company_custom'],
            rules: [{ required: true }],
            step: 0
          }
        ]
      : []),
    ...(formValues && formValues.termination_by_company
      ? [
          {
            name: ['termination_date_by_company'],
            input: <DatePicker format={dateFormat} style={{ minWidth: 200 }} />,
            step: 0
          }
        ]
      : []),
    {
      name: ['deductible_cost_index'],
      tooltip: !!(formValues && formValues.empty_unoccupied_works),
      rules: [{ required: true }],
      input: (
        <Select>
          {enums?.costIndexesPnoml && formValues ? (
            enums.costIndexesPnoml
              .filter((e, index) => {
                if (
                  formValues?.main_use === 'rec_OPEN_PARKING' ||
                  formValues?.main_use === 'rec_BOX_PARKING'
                ) {
                  return e === '0,25x';
                }
                return !formValues.empty_unoccupied_works || index >= 6;
              })
              .map(
                (e) =>
                  e !== '45x' && (
                    <Option value={e} key={e}>
                      {t(`contracts.tags.${e}`)}
                    </Option>
                  )
              )
          ) : (
            <Option value="4,5x">{t(`contracts.tags.${'4,5x'}`)}</Option>
          )}
        </Select>
      ),
      help: exemptions.includes('deductible_cost_index')
        ? t('help.exemptions')
        : null,
      step: 0
    },
    {
      name: ['property_under_construction'],
      input: (
        <PreRequisitesSwitch
          onChange={(value) => handlePropertyUnderConstructionChange(value)}
        />
      ),
      valuePropName: 'checked',
      step: 0
    },
    ...(formValues && formValues.property_under_construction
      ? [
          {
            name: ['under_construction_duration'],
            rules: [{ required: true }],
            input: (
              <>
                <Form.Item name="under_construction_duration" noStyle>
                  <Radio.Group buttonStyle="solid" className="radio-with-icon">
                    <Radio.Button
                      value="under_construction_minus4"
                      style={{ width: 130 }}
                    >
                      <ConstructionIcon />
                      {t('contracts.tags.under_construction_minus4')}
                    </Radio.Button>
                    <Radio.Button
                      value="under_construction_plus4"
                      style={{ width: 130 }}
                    >
                      <ConstructionLongIcon />
                      {t('contracts.tags.under_construction_plus4')}
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </>
            ),
            step: 0
          }
        ]
      : []),
    {
      step: 0,
      name: ['comparisonMRI'],
      noStyle: true,
      noLabel: true,
      layout: {
        labelCol: { span: 0 },
        wrapperCol: { xs: 24, sm: 24, md: 24, lg: 24, xl: 24 }
      },
      input: (
        <ComparisonTableMRI
          form={form}
          type="pnoml"
          fields={[
            { name: ['g1231_fire_related_risks'] },
            { name: ['g0230_motorized_vehicle_impact'] },
            { name: ['g1231_natural_disasters'] },
            { name: ['g1231_technological_risks'] },
            { name: ['g1231_attacks'] },
            { name: ['g0230_climatic_events'] },
            { name: ['g0230_theft_vandalism'] },
            { name: ['g0030_water_damage'] },
            { name: ['g0030_broken_glass'] },
            { name: ['g1231_civil_liability'] },
            { name: ['g1231_criminal_defense_recourse'] }
          ]}
          columns={[{ title: 'TYPE1' }]}
        />
      )
    },
    {
      name: ['display_furnished_rental'],
      tooltip: true,
      input: (
        <Switch
          checkedChildren={t('quotes.form.switch.yes')}
          unCheckedChildren={t('quotes.form.switch.no')}
          onChange={(value) => handleDisplayFurnishedRentalChange(value)}
        />
      ),
      step: 0
    },
    ...(displayFurnishedRental
      ? [
          {
            name: ['furnished_rental'],
            tooltip: false,
            input: (
              <Radio.Group buttonStyle="solid">
                {enums?.furnishedOptions &&
                  enums.furnishedOptions
                    .filter((option) => option > 0)
                    .map((option) => (
                      <Radio.Button key={option} value={option}>
                        {`${numeral(option).format('0,0')}€`}
                      </Radio.Button>
                    ))}
              </Radio.Group>
            ),
            help: exemptions.includes('furnished_rental')
              ? t('help.exemptions')
              : null,
            step: 0
          }
        ]
      : []),

    ...(formValues &&
    (formValues.main_use === 'rec_APPARTMENT' ||
      formValues.main_use === 'rec_HOUSE')
      ? [
          {
            name: ['squat_option'],
            tooltip: true,
            input: (
              <Switch
                checkedChildren={t('quotes.form.switch.yes')}
                unCheckedChildren={t('quotes.form.switch.no')}
              />
            ),
            step: 0
          }
        ]
      : []),
    {
      name: ['legal_protection'],
      label: 'legal_protection_option',
      tooltip: true,
      tooltipName: 'legal_protection_pnoml',
      input: (
        <Switch
          checkedChildren={t('quotes.form.switch.yes')}
          unCheckedChildren={t('quotes.form.switch.no')}
        />
      ),
      step: 0
    },
    {
      name: ['fraction_type'],
      rules: [{ required: true }],
      tooltip: true,
      tooltipName: 'fraction_type_pnoml',
      input: (
        <Radio.Group buttonStyle="solid" disabled={isAmendment !== -1}>
          <Radio.Button value="YEARLY">
            {t('contracts.form.options.YEARLY')}
          </Radio.Button>
        </Radio.Group>
      ),
      step: 0
    },
    {
      name: ['property_in_castle_or_listed_building'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_has_prohibited_restaurant_activities'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['risk_has_industrial_activities'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['tenant_in_restructuring_or_liquidation'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['property_under_construction_prerequisite'],
      valuePropName: 'checked',
      step: 1
    },
    ...(formValues?.main_use === 'rec_COMMERCIAL_PREMISE'
      ? [
          {
            name: ['no_prohibited_activities_declared'],
            valuePropName: 'checked',
            step: 1
          }
        ]
      : []),
    {
      name: ['property_in_renovation_over_4_months'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['property_in_historic_building'],
      valuePropName: 'checked',
      step: 1
    },
    {
      name: ['property_previously_insured'],
      valuePropName: 'checked',
      step: 1
    },
    ...(formValues && !formValues.property_previously_insured
      ? [
          {
            name: ['have_you_owned_property_for_more_than_2_months'],
            valuePropName: 'checked',
            step: 1
          }
        ]
      : []),
    {
      name: ['is_property_occupied_or_will_be_within_2_months'],
      valuePropName: 'checked',
      step: 1
    }
  ];
  return {
    pnomlFields: pnomlFields.map((f) => {
      if (f.step === 1)
        return {
          ...f,
          input: (
            <PreRequisitesSwitch
              onChange={(val) => {
                setFormValues({
                  ...formValues,
                  [f.name[0]]: val
                });
                setCheckedVal(f.name[0]);
              }}
            />
          ),
          valuePropName: 'checked',
          labelAlign: 'left',
          withDivider: true,
          layout: preRequisitesLayout
        };
      return f;
    })
  };
};

export default usePNOMLFields;
